/**
 * Is this a company URL a paid audience build can be run against?
 *
 * The previous check was `hostname.includes('.') && hostname.length > 3`, which accepts
 * `.com` and `https://.com`: both parse, both contain a dot, both are 4 characters. So a
 * meaningless URL enabled Continue and reached the paid build (#475, F13-03).
 *
 * Deliberately structural rather than a TLD allowlist — a list would need maintaining and
 * would reject legitimate new TLDs. What it rejects is a hostname that cannot name a real
 * site: an empty label, no TLD, or a numeric/one-character TLD.
 */

/**
 * Each dot-separated label is alphanumeric with internal hyphens, and the final label (the
 * TLD) is at least two letters. `.com` fails on the empty first label; `example` fails for
 * having no TLD; `example.c` and `example.12` fail on the TLD.
 */
const HOSTNAME_RE =
  /^(?!-)[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.(?!-)[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)*\.[a-z]{2,}$/i;

export function isValidCompanyUrl(raw: string): boolean {
  const trimmed = raw.trim();
  if (!trimmed) return false;

  try {
    // Users type "acme.com", not "https://acme.com" — prepend so the parser can work,
    // but only when no scheme is present, so "ftp://x" is not silently rewritten.
    const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    const url = new URL(withScheme);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
    return HOSTNAME_RE.test(url.hostname);
  } catch {
    return false;
  }
}
